import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import MainLayout from '@/features/common/components/MainLayout';
import LoadingScreen from '@/features/common/pages/LoadingScreen';
import { downloadCV } from '@/services/interview';

export default function CVDownloadPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!id) return;

    const downloadPdf = async () => {
      try {
        setIsDownloading(true);
        const pdf = await downloadCV(id);

        // Create a blob URL from the PDF blob
        const url = URL.createObjectURL(pdf);

        // Create an anchor element and trigger download
        const a = document.createElement('a');
        a.href = url;
        a.download = `cv-${id}.pdf`;
        document.body.appendChild(a);
        a.click();

        // Clean up
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          setIsDownloading(false);
        }, 100);
      } catch (err) {
        console.error('Error downloading PDF:', err);
        Sentry.captureException(err, {
          tags: { feature: 'cv-download-page' },
          extra: { cvId: id },
        });
        setError(t('DownloadCV.error'));
      } finally {
        setIsDownloading(false);
      }
    };

    downloadPdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isDownloading) {
    return <LoadingScreen />;
  }

  return (
    <MainLayout>
      {error ? (
        <div className="text-xl text-red-500">{error}</div>
      ) : (
        <div className="text-xl text-primary">{t('DownloadCV.success')}</div>
      )}
    </MainLayout>
  );
}
